import { Plus, Trash2 } from 'lucide-react'
import { useMemo } from 'react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import type { PermissionSource } from '@/types/api/permission'
import { cn } from '@/utils/cn'

import {
  SCOPE_BADGE_CLASSES,
  SCOPE_LABELS,
  includedKeys,
  instanceGrantableActions,
  sourceLabel,
  type CatalogIndex,
  type ScopedGrantRow,
} from './permissionCatalog'

interface PermissionTabsProps {
  index: CatalogIndex
  /** Workspace-wide keys granted directly to this role / user (the checkboxes). */
  selected: Set<string>
  /** Workspace-wide keys this user already gets elsewhere (roles, admin, owner) — user page only. */
  inherited?: Map<string, PermissionSource[]>
  onToggle: (key: string) => void
  onToggleMany: (keys: string[], value: boolean) => void
  /** Grants on specific resources. */
  scopedGrants: ScopedGrantRow[]
  resolveName: (row: ScopedGrantRow) => string
  onRevoke: (row: ScopedGrantRow) => void
  onAddScoped: (category: string) => void
  /** Whether the viewer may grant this key (escalation guard: only what they hold). */
  canGrantKey: (key: string) => boolean
  readOnly?: boolean
  revoking?: boolean
}

/**
 * Permissions grouped into one tab per area (categories come from the backend catalog).
 * Each tab has workspace-wide toggles and the grants on specific items for that area.
 */
export function PermissionTabs({
  index,
  selected,
  inherited,
  onToggle,
  onToggleMany,
  scopedGrants,
  resolveName,
  onRevoke,
  onAddScoped,
  canGrantKey,
  readOnly = false,
  revoking = false,
}: PermissionTabsProps) {
  const categories = index.categories
  const labelOf = (key: string) => index.actionByKey.get(key)?.label ?? key
  // Keys included in what is checked or inherited (e.g. "Edit records" includes "View records").
  const included = useMemo(
    () => includedKeys(index, [...selected, ...(inherited?.keys() ?? [])]),
    [index, selected, inherited]
  )
  const countByCategory = useMemo(() => {
    const counts = new Map<string, number>()
    for (const resource of index.catalog.resources) {
      const granted = resource.actions.filter((a) => selected.has(a.key) || inherited?.has(a.key)).length
      const scoped = scopedGrants.filter((g) => index.resourceByKey.get(g.permissionKey) === resource).length
      counts.set(resource.category, (counts.get(resource.category) ?? 0) + granted + scoped)
    }
    return counts
  }, [index, selected, inherited, scopedGrants])

  if (categories.length === 0) return null

  return (
    <TooltipProvider>
      <Tabs defaultValue={categories[0].id}>
        <div className="-mx-1 overflow-x-auto px-1 pb-1">
          <TabsList className="h-auto w-max flex-nowrap justify-start">
            {categories.map((category) => (
              <TabsTrigger key={category.id} value={category.id} className="gap-2">
                {category.label}
                {(countByCategory.get(category.id) ?? 0) > 0 && (
                  <span className="rounded-full bg-primary-100 px-1.5 text-[10px] font-semibold text-primary-700">
                    {countByCategory.get(category.id)}
                  </span>
                )}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        {categories.map((category) => {
          const resources = index.catalog.resources.filter((r) => r.category === category.id)
          const editableKeys = resources
            .flatMap((r) => r.actions.map((a) => a.key))
            .filter((key) => !inherited?.has(key) && canGrantKey(key))
          const allSelected = editableKeys.length > 0 && editableKeys.every((k) => selected.has(k))
          const rows = scopedGrants.filter((g) => index.resourceByKey.get(g.permissionKey)?.category === category.id)
          const supportsScoped = instanceGrantableActions(index, category.id).length > 0

          return (
            <TabsContent key={category.id} value={category.id} className="space-y-6 pt-2">
              <section className="space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <h3 className="text-sm font-semibold text-neutral-800">Workspace-wide</h3>
                    <p className="text-xs text-neutral-500">Applies across the whole workspace.</p>
                  </div>
                  {!readOnly && editableKeys.length > 0 && (
                    <button
                      type="button"
                      className="text-xs text-primary-600 hover:underline"
                      onClick={() => onToggleMany(editableKeys, !allSelected)}
                    >
                      {allSelected ? 'Clear all' : 'Select all'}
                    </button>
                  )}
                </div>

                {resources.map((resource) => (
                  <div key={resource.resourceType} className="space-y-2">
                    <h4 className="text-xs font-semibold uppercase tracking-wide text-neutral-500">{resource.label}</h4>
                    <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                      {resource.actions.map((action) => {
                        const inheritedFrom = inherited?.get(action.key)
                        const includedIn = included.get(action.key)
                        // Checkbox = direct grant; inherited access is shown as badges and highlights the tile.
                        // A key included in another held key is shown checked and locked: it cannot be
                        // removed without removing the key that includes it.
                        const isDirect = selected.has(action.key)
                        const isIncludedOnly = !isDirect && !!includedIn
                        const isOn = isDirect || !!inheritedFrom || !!includedIn
                        const grantable = canGrantKey(action.key)
                        const disabled = readOnly || isIncludedOnly || (!grantable && !isDirect)
                        return (
                          <label
                            key={action.key}
                            className={cn(
                              'flex items-start gap-2 rounded-lg border p-3 transition-colors',
                              disabled ? 'cursor-default' : 'cursor-pointer',
                              isOn
                                ? 'border-primary-300 bg-primary-50'
                                : 'border-neutral-200 bg-white hover:bg-neutral-50'
                            )}
                          >
                            <Checkbox
                              className="mt-0.5"
                              checked={isDirect || isIncludedOnly}
                              disabled={disabled}
                              onCheckedChange={() => onToggle(action.key)}
                            />
                            <span className="min-w-0 space-y-0.5">
                              <span className="block text-sm font-medium text-neutral-800">{action.label}</span>
                              <span className="block text-xs text-neutral-500">{action.description}</span>
                              <span className="flex flex-wrap gap-1 pt-1">
                                {inheritedFrom?.map((source, i) => (
                                  <Badge key={i} variant="secondary" className="text-[10px]">
                                    {sourceLabel(source, labelOf)}
                                  </Badge>
                                ))}
                                {includedIn && !inheritedFrom && (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Badge variant="secondary" className="text-[10px]">
                                        Included in {includedIn.map(labelOf).join(', ')}
                                      </Badge>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      Granted automatically with {includedIn.map(labelOf).join(', ')}.
                                    </TooltipContent>
                                  </Tooltip>
                                )}
                                {action.billingSensitive && (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Badge variant="outline" className="text-[10px]">Owner only</Badge>
                                    </TooltipTrigger>
                                    <TooltipContent>Admins do not get this automatically; grant it explicitly.</TooltipContent>
                                  </Tooltip>
                                )}
                                {!grantable && !readOnly && !isDirect && (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Badge variant="outline" className="text-[10px] text-neutral-500">Not grantable</Badge>
                                    </TooltipTrigger>
                                    <TooltipContent>You can only grant permissions you hold yourself.</TooltipContent>
                                  </Tooltip>
                                )}
                              </span>
                            </span>
                          </label>
                        )
                      })}
                    </div>
                  </div>
                ))}
              </section>

              {(supportsScoped || rows.length > 0) && (
                <section className="space-y-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <h3 className="text-sm font-semibold text-neutral-800">Specific items</h3>
                      <p className="text-xs text-neutral-500">Access limited to particular record groups or items.</p>
                    </div>
                    {!readOnly && supportsScoped && (
                      <Button size="sm" variant="outline" className="gap-1.5" onClick={() => onAddScoped(category.id)}>
                        <Plus className="h-4 w-4" />
                        Add access
                      </Button>
                    )}
                  </div>
                  {rows.length === 0 ? (
                    <p className="rounded-lg border border-dashed border-neutral-200 py-6 text-center text-xs text-neutral-400">
                      No access to specific items.
                    </p>
                  ) : (
                    <div className="overflow-x-auto rounded-lg border border-neutral-200">
                      <table className="w-full min-w-[560px] border-collapse text-left text-xs">
                        <thead>
                          <tr className="border-b border-neutral-100 bg-neutral-50 text-[11px] font-semibold uppercase tracking-wide text-neutral-500">
                            <th className="px-3 py-2">Permission</th>
                            <th className="px-3 py-2">Applies to</th>
                            <th className="px-3 py-2">Scope</th>
                            {inherited && <th className="px-3 py-2">Source</th>}
                            <th className="px-3 py-2 text-right" />
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-neutral-100">
                          {rows.map((row) => {
                            const action = index.actionByKey.get(row.permissionKey)
                            return (
                              <tr key={row.rowKey} className="hover:bg-neutral-50/50">
                                <td className="px-3 py-2 font-medium text-neutral-800">{action?.label ?? row.permissionKey}</td>
                                <td className="px-3 py-2 text-neutral-600">{resolveName(row)}</td>
                                <td className="px-3 py-2">
                                  <Badge variant="outline" className={cn('border text-[10px]', SCOPE_BADGE_CLASSES[row.scope])}>
                                    {SCOPE_LABELS[row.scope]}
                                  </Badge>
                                </td>
                                {inherited && (
                                  <td className="px-3 py-2">
                                    <div className="flex flex-wrap gap-1">
                                      {row.sources.map((s, i) => (
                                        <Badge key={i} variant={s.type === 'OVERRIDE' ? 'outline' : 'secondary'} className="text-[10px]">
                                          {sourceLabel(s, labelOf)}
                                        </Badge>
                                      ))}
                                    </div>
                                  </td>
                                )}
                                <td className="px-3 py-2 text-right">
                                  {!readOnly && row.revokeGrantId && (
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      aria-label="Revoke"
                                      disabled={revoking}
                                      onClick={() => onRevoke(row)}
                                      className="h-7 w-7 text-red-500 hover:bg-red-50 hover:text-red-700"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  )}
                                </td>
                              </tr>
                            )
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </section>
              )}
            </TabsContent>
          )
        })}
      </Tabs>
    </TooltipProvider>
  )
}
